import {restRequest} from '@nlabs/metropolisjs';

import {getOneOffVideoFingerprint, getPendingOneOffVideo} from './workspaceSelectors.js';

import type {FluxFramework} from '@nlabs/arkhamjs';
import type {CreativeRequest, GenerationRequest, OneOffVideoRequest, Workspace} from '@reactorbox/shared';

export const WORKSPACE_EVENTS = {CHANGED: 'REACTORBOX_WORKSPACE_CHANGED', ERROR: 'REACTORBOX_WORKSPACE_ERROR', PENDING: 'REACTORBOX_WORKSPACE_PENDING', SELECTED: 'REACTORBOX_WORKSPACE_SELECTED'} as const;
export type View = 'projects' | 'studio' | 'characters' | 'assets' | 'billing' | 'videos';
export interface Selection {
  readonly createProject?: boolean;
  readonly projectId?: string;
  readonly view: View;
}

export const getGenerationIntent = (
  path: string, request: CreativeRequest | GenerationRequest | OneOffVideoRequest
): string => {
  if(path === '/api/creative') {
    const {idempotencyKey: _key, ...intent} = request as CreativeRequest;
    return JSON.stringify([path, intent]);
  }
  if(path === '/api/videos') {
    const video = request as OneOffVideoRequest;
    return `${path}:${getOneOffVideoFingerprint(video)}`;
  }
  const generation = request as GenerationRequest;
  return JSON.stringify([path, generation.projectId, generation.shotId, generation.kind]);
};

export const createWorkspaceActions = (flux: FluxFramework, navigate?: (selection: Selection) => void) => {
  const epoch = flux.getState('auth.epoch', 0);
  const current = (): boolean => flux.getState('auth.epoch', 0) === epoch;
  const inFlight = new Set<string>();
  const request = async (path: string, method = 'GET', body?: unknown, notice?: string): Promise<boolean> => {
    if(!current()) {
      return false;
    }
    await flux.dispatch({type: WORKSPACE_EVENTS.PENDING});
    try {
      // Metropolis uses Rip-Hunter for HTTP; no independent request implementation.
      const response = await restRequest<Workspace | {error: string}>(flux, path, method, body, {queueOffline: false});
      if(!current()) {
        return false;
      }
      if(response && 'error' in response) {
        throw new Error(response.error);
      }
      const workspace = response;
      if(!workspace || !Array.isArray(workspace.projects)) {
        throw new Error('The studio returned an incomplete response. Please refresh.');
      }
      await flux.setState('workspace.data', workspace);
      await flux.setState('workspace.error', '');
      await flux.dispatch({notice, type: WORKSPACE_EVENTS.CHANGED});
      return true;
    } catch(error) {
      if(!current()) {
        return false;
      }
      const message = error instanceof Error ? error.message : 'Unable to reach the studio. Please try again.';
      await flux.setState('workspace.error', message);
      await flux.dispatch({error: message, type: WORKSPACE_EVENTS.ERROR});
      return false;
    }
  };
  return {
    load: () => request('/api/workspace'),
    mutate: async (path: string, body: unknown, notice: string, method = 'POST'): Promise<void> => {
      if(!current()) {
        return;
      }
      if(path !== '/api/generations' && path !== '/api/videos' && path !== '/api/creative') {
        await request(path, method, body, notice);
        return;
      }
      const generation = body as GenerationRequest | OneOffVideoRequest;
      const snapshot = flux.getState('workspace.data') as Workspace | undefined;
      if(path === '/api/videos' && snapshot && getPendingOneOffVideo(snapshot, generation as OneOffVideoRequest)) {
        return;
      }
      const intent = getGenerationIntent(path, generation);
      if(inFlight.has(intent)) {
        return;
      }
      inFlight.add(intent);
      try {
        const pending = flux.getState('workspace.generationIntents', {}) as Record<string, string>;
        const key = pending[intent] || generation.idempotencyKey;
        await flux.setState('workspace.generationIntents', {...pending, [intent]: key});
        const confirmed = await request(path, method, {...generation, idempotencyKey: key}, notice);
        if(confirmed && current()) {
          const remaining = {...flux.getState('workspace.generationIntents', {}) as Record<string, string>};
          delete remaining[intent];
          await flux.setState('workspace.generationIntents', remaining);
        }
      } finally {
        inFlight.delete(intent);
      }
    },
    select: async (selection: Selection): Promise<void> => {
      if(!current()) {
        return;
      }
      await flux.setState('workspace.selection', selection);
      await flux.dispatch({type: WORKSPACE_EVENTS.SELECTED});
      navigate?.(selection);
    }
  };
};
export type WorkspaceActions = ReturnType<typeof createWorkspaceActions>;
